import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { CollapsibleList } from 'ui-components/components/collapsible-list';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CurrencyPropType } from '../../../../prop-types/currency';
import { emptyArray } from 'ui-components/default-props/empty';
import CostReviewLineItemService from '../../../cost-review-line-item/cost-review-line-item-service';
import FormattedCurrencyContainer from '../../../formatted-currency/container';
import style from './additional-charges-collapsible.module.less';

export const AdditionalChargesCollapsible = ({ lineItems = emptyArray }) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);
  const subTotal = lineItems.reduce(
    (subtotal, charge) => {
      return {
        code: charge.amount.code,
        value: subtotal.value + charge.amount.value,
      };
    },
    { code: '', value: 0 },
  );

  const onCollapsibleToggle = useCallback(() => {
    setIsSectionExpanded(!isSectionExpanded);
  }, [setIsSectionExpanded, isSectionExpanded]);

  const processedLineItems = lineItems.reduce((grouped, surcharge) => {
    const groupedLineItems = [...grouped];
    const existingIndex = groupedLineItems.findIndex(
      (item) => item.description === surcharge.description && item.category === surcharge.category && item.code === surcharge.amount.code,
    );

    if (existingIndex !== -1) {
      groupedLineItems[existingIndex].value += surcharge.amount.value;
    } else {
      groupedLineItems.push({
        id: surcharge.id,
        category: surcharge.category,
        description: surcharge.description,
        code: surcharge.amount.code,
        value: surcharge.amount.value,
      });
    }

    return groupedLineItems;
  }, []);

  return (
    <CollapsibleList className={style['additional-charges-collapsible']}>
      <CollapsibleListSection className={style['additional-charges-collapsible-list-section']} open={isSectionExpanded}>
        <CollapsibleListSectionHeader
          className={style['additional-charges-collapsible-section-header']}
          onClick={onCollapsibleToggle}
          openExpanderClassName={style['additional-charges-collapsible-section-header-expander']}
          theme='white'
        >
          <div className={style['additional-charges-collapsible-section-header-content']}>
            <div>
              <FormattedMessage
                defaultMessage='Additional Charges'
                description='costReview.additionalChargesCollapsible.headerTitle'
                id='aZAHcT'
              />
            </div>
            {!isSectionExpanded && (
              <div>
                <FormattedCurrencyContainer currencyCode={subTotal.code} value={subTotal.value} />
              </div>
            )}
          </div>
        </CollapsibleListSectionHeader>
        <CollapsibleListSectionContent className={style['additional-charges-collapsible-section-content']}>
          {processedLineItems.map(({ id, description, code, value }) => (
            <CostReviewLineItemService key={id} serviceCost={value} serviceCurrencyCode={code} serviceName={description} />
          ))}
        </CollapsibleListSectionContent>
      </CollapsibleListSection>
    </CollapsibleList>
  );
};

AdditionalChargesCollapsible.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      amount: CurrencyPropType.isRequired,
    }),
  ),
};
